import { QueryClient } from '@tanstack/react-query';
import { processApiError } from '../utils/reactQueryErrorHandler';
import { AlertTypes } from '../data/options';
import { triggerAuthFailure } from '../utils/authFailureHandler';

let globalSetAlert = null;

export const setGlobalAlertHandler = (setAlert) => {
  globalSetAlert = setAlert;
};

const isAuthError = (error) => {
  const status = error?.response?.status || error?.status;
  if (status === 401) return true;
  const msg = error?.message || '';
  return msg.includes('401') || msg.toLowerCase().includes('session expired');
};

const handleQueryError = (error, query) => {
  if (isAuthError(error)) {
    triggerAuthFailure();
    return;
  }

  const setAlert = query.meta?.setAlert || globalSetAlert;
  
  if (setAlert) {
    const errorMessage = processApiError(error);
    console.error('React Query Error:', error);
    
    setAlert({
      type: AlertTypes.ERROR,
      msg: errorMessage,
      visible: true,
    });
  }
};

const handleMutationError = (error, variables, context, mutation) => {
  if (isAuthError(error)) {
    triggerAuthFailure();
    return;
  }

  const setAlert = mutation.meta?.setAlert || globalSetAlert;
  
  if (setAlert) {
    const errorMessage = processApiError(error);
    console.error('React Query Mutation Error:', error);
    
    setAlert({
      type: AlertTypes.ERROR,
      msg: errorMessage,
      visible: true,
    });
  }
};

const MINUTE = 60 * 1000;

// Freshness policy per query family. Spread into the useQuery options of each
// hook family so the trade-off is explicit where the data is defined:
//   reference — slow-changing lookups (rooms, tenants): rarely refetch.
//   analytics — inventory / performance / overview RPC snapshots: heavy to
//               compute and only change as sales come in, so a 10 minute
//               window is fine.
//   editable  — alerts and filter groups the user edits in-app: refetch on
//               (re)mount so a list never shows a stale row after navigating
//               back from a details page.
// refetchOnMount is left at the react-query default (refetch when stale),
// so a plain invalidateQueries() is enough after a mutation — inactive
// queries are marked stale and refetch the next time they mount.
export const CACHE_POLICY = {
  reference: { staleTime: 30 * MINUTE, gcTime: 60 * MINUTE },
  analytics: { staleTime: 10 * MINUTE, gcTime: 15 * MINUTE },
  editable: { staleTime: 30 * 1000, gcTime: 15 * MINUTE },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

queryClient.getQueryCache().config.onError = handleQueryError;
queryClient.getMutationCache().config.onError = handleMutationError;
